import React from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 12,
  border: "1px solid",
  borderColor: theme.palette.divider,
  transition: "all 0.3s ease",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    boxShadow: theme.shadows[4],
    transform: "translateY(-2px)",
  },
}));

const MetricCard = ({ title, value, previousValue, formatCurrency = true, trend, showMTD = false, showYTD = false }) => {
  const formatValue = (val) => {
    if (formatCurrency) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val);
    }
    return val?.toLocaleString() || "0";
  };

  const calculateChange = () => {
    if (!previousValue || previousValue === 0) return null;
    if (value === 0 && previousValue === 0) return null;
    const change = ((value - previousValue) / previousValue) * 100;
    return change.toFixed(2);
  };

  const changePercent = calculateChange();
  const isPositive = changePercent ? parseFloat(changePercent) > 0 : null;

  return (
    <StyledCard elevation={0}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
            fontSize: "0.875rem",
          }}
        >
          {title}
        </Typography>
        {showMTD && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              fontWeight: 500,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: "action.hover",
            }}
          >
            MTD
          </Typography>
        )}
        {showYTD && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
              fontWeight: 500,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: "action.hover",
            }}
          >
            YTD
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "text.primary",
            fontSize: "1.75rem",
          }}
        >
          {formatValue(value)}
        </Typography>
        {changePercent !== null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: isPositive ? "error.main" : "success.main",
            }}
          >
            {isPositive ? (
              <TrendingUpIcon sx={{ fontSize: 18 }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 18 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              {Math.abs(parseFloat(changePercent))}%
            </Typography>
          </Box>
        )}
      </Box>
      {previousValue && previousValue !== 0 && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            mt: "auto",
          }}
        >
          Previous: {formatValue(previousValue)}
        </Typography>
      )}
      {(!previousValue || previousValue === 0) && (
        <Box sx={{ mt: "auto", height: "1rem" }} />
      )}
    </StyledCard>
  );
};

const FinOpsSummaryCards = ({ data }) => {
  if (!data) return null;

  const {
    currentMonthToDateUsage = 0,
    yearToDateUsage = 0,
    lastMonthUsage = 0,
    todayUsage = 0,
    yesterdayUsage = 0,
    prevMonthToSameDateUsage = 0,
  } = data;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <MetricCard
          title="Yesterday"
          value={yesterdayUsage}
          previousValue={null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <MetricCard
          title="Current Month"
          value={currentMonthToDateUsage}
          previousValue={prevMonthToSameDateUsage && prevMonthToSameDateUsage !== 0 ? prevMonthToSameDateUsage : null}
          showMTD={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <MetricCard
          title="Year to Date"
          value={yearToDateUsage}
          previousValue={null}
          showYTD={true}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <MetricCard
          title="Last Month"
          value={lastMonthUsage}
          previousValue={null}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <MetricCard
          title="Marketplace Spend"
          value={data.cumulativeMarketplaceSpend || 0}
          previousValue={null}
        />
      </Grid>
    </Grid>
  );
};

export default FinOpsSummaryCards;

